import { AlumniFilters, RangeValue, ToggleValue } from './BurnerAnalysisService';

interface Props {
    filters: AlumniFilters;
    completionYears: number[];
    programLevels: string[];
    onChange: (next: AlumniFilters) => void;
    show: boolean;
}

const RANGES: { value: RangeValue; label: string }[] = [
    { value: '', label: 'Any time' },
    { value: '1m', label: '1 month' },
    { value: '3m', label: '3 months' },
    { value: '6m', label: '6 months' },
];

const CP_OPTIONS: { value: string; label: string }[] = [
    { value: 'lt40', label: 'Less than 40%' },
    { value: '4059', label: '40–59%' },
    { value: '6074', label: '60–74%' },
    { value: '75plus', label: '75% and above' },
];

const Toggle = ({
    value,
    label,
    onChange,
}: {
    value: ToggleValue;
    label: string;
    onChange: (v: ToggleValue) => void;
}) => {
    const opts: { v: ToggleValue; lbl: string; bg: string; fg: string }[] = [
        { v: '', lbl: 'Any', bg: '#e6f1fb', fg: '#0c447c' },
        { v: 'yes', lbl: 'Yes', bg: '#eaf3de', fg: '#27500a' },
        { v: 'no', lbl: 'No', bg: '#fcebeb', fg: '#791f1f' },
    ];
    return (
        <div>
            <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                {label}
            </div>
            <div
                style={{
                    display: 'flex',
                    border: '0.5px solid rgba(15,23,42,0.15)',
                    borderRadius: 8,
                    overflow: 'hidden',
                }}>
                {opts.map((o, i) => {
                    const active = value === o.v;
                    return (
                        <button
                            key={o.v}
                            type="button"
                            onClick={() => onChange(value === o.v ? '' : o.v)}
                            style={{
                                flex: 1,
                                padding: '6px 4px',
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: 'pointer',
                                border: 'none',
                                borderRight: i < 2 ? '0.5px solid rgba(15,23,42,0.08)' : 'none',
                                background: active ? o.bg : '#fff',
                                color: active ? o.fg : '#64748b',
                            }}>
                            {o.lbl}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const SectionDivider = ({ label }: { label: string }) => (
    <div
        className="text-uppercase fw-bold text-muted mt-3 mb-2 pb-2 border-bottom"
        style={{ fontSize: 11, letterSpacing: 0.5 }}>
        {label}
    </div>
);

const AlumniFiltersPanel = ({ filters, completionYears, programLevels, onChange, show }: Props) => {
    const update = (patch: Partial<AlumniFilters>) => {
        onChange({ ...filters, ...patch, offset: 0 });
    };

    const toggleCp = (value: string) => {
        const current = filters.profileCompletionRanges ?? [];
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        update({ profileCompletionRanges: next });
    };

    const onToggleOutreach = (v: ToggleValue) => {
        update({ outreachExists: v, outreachRange: v === 'yes' ? filters.outreachRange ?? '' : '' });
    };

    const onToggleEngagement = (v: ToggleValue) => {
        update({ engagementExists: v, engagementRange: v === 'yes' ? filters.engagementRange ?? '' : '' });
    };

    if (!show) return null;

    return (
        <>
            <SectionDivider label="Program" />
            <div className="row g-3 mb-0">
                <div className="col-md-3">
                    <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                        Program Level
                    </div>
                    <select
                        className="form-select"
                        value={filters.programLevel ?? ''}
                        onChange={(e) => update({ programLevel: e.target.value })}>
                        <option value="">All levels</option>
                        {programLevels.map((lvl) => (
                            <option key={lvl} value={lvl}>
                                {lvl}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-3">
                    <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                        Completion Year
                    </div>
                    <select
                        className="form-select"
                        value={String(filters.passOutYear ?? '')}
                        onChange={(e) => update({ passOutYear: e.target.value })}>
                        <option value="">All years</option>
                        {completionYears.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <SectionDivider label="Profile completeness" />
            <div className="row g-3">
                <div className="col-md-3">
                    <Toggle
                        value={filters.linkedinExists ?? ''}
                        label="LinkedIn URL exists"
                        onChange={(v) => update({ linkedinExists: v })}
                    />
                </div>
                <div className="col-md-3">
                    <Toggle
                        value={filters.profilePictureExists ?? ''}
                        label="Profile picture exists"
                        onChange={(v) => update({ profilePictureExists: v })}
                    />
                </div>
                <div className="col-md-6">
                    <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                        Profile completion %
                    </div>
                    <div
                        className="d-flex flex-wrap"
                        style={{
                            gap: 14,
                            padding: '7px 10px',
                            border: '0.5px solid rgba(15,23,42,0.15)',
                            borderRadius: 8,
                            background: '#fff',
                        }}>
                        {CP_OPTIONS.map((opt) => (
                            <label
                                key={opt.value}
                                className="d-flex align-items-center font-13"
                                style={{ gap: 6, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={(filters.profileCompletionRanges ?? []).includes(opt.value)}
                                    onChange={() => toggleCp(opt.value)}
                                    style={{ accentColor: '#185fa5', cursor: 'pointer' }}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <SectionDivider label="Activity logs" />
            <div className="row g-3">
                <div className="col-md-3">
                    <Toggle
                        value={filters.outreachExists ?? ''}
                        label="Outreach log exists"
                        onChange={onToggleOutreach}
                    />
                </div>
                {filters.outreachExists === 'yes' && (
                    <div className="col-md-3">
                        <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                            Outreach in last
                        </div>
                        <select
                            className="form-select"
                            value={filters.outreachRange ?? ''}
                            onChange={(e) => update({ outreachRange: e.target.value as RangeValue })}>
                            {RANGES.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="col-md-3">
                    <Toggle
                        value={filters.engagementExists ?? ''}
                        label="Engagement log exists"
                        onChange={onToggleEngagement}
                    />
                </div>
                {filters.engagementExists === 'yes' && (
                    <div className="col-md-3">
                        <div className="font-11 fw-bold text-uppercase text-muted mb-1" style={{ letterSpacing: 0.5 }}>
                            Engagement in last
                        </div>
                        <select
                            className="form-select"
                            value={filters.engagementRange ?? ''}
                            onChange={(e) => update({ engagementRange: e.target.value as RangeValue })}>
                            {RANGES.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        </>
    );
};

export default AlumniFiltersPanel;
